import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import { PremiumTable, CopyButton } from "../../../../Components/Others/PremiumTable";

// Bank Table Row Component
const BankTableRow = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit =
    GetAccess("bank_details_edit") || GetAccess("banks_edit") || GetRole();
  const canDelete =
    GetAccess("bank_details_delete") || GetAccess("banks_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/banks/${row.id}`, setDeleteConfirmOpen);
  };

  return (
    <>
      <tr className="bg-white hover:bg-white/80 transition-colors align-middle">
        {/* Bank Name */}
        <td className="p-4 align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl">
          <Link
            to={`/payments/banks/view/${row.id}`}
            state={{ data: row, pageIndex, searchQuery }}
            className="hover:underline text-xs 2xl:text-sm font-semibold text-gray-900 block"
          >
            {row.bankName || "N/A"}
          </Link>
        </td>

        {/* Account Name */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs 2xl:text-sm text-gray-700 font-medium">
            {row.accountName || "N/A"}
          </span>
        </td>

        {/* Account Number */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex items-center text-xs 2xl:text-sm font-semibold text-gray-900">
            <span>{row.accountNumber || "N/A"}</span>
            {row.accountNumber && <CopyButton text={row.accountNumber} />}
          </div>
        </td>

        {/* IBAN */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex items-center text-xs 2xl:text-sm text-gray-700 font-mono">
            <span className="truncate max-w-[180px]">{row.iban || "-"}</span>
            {row.iban && <CopyButton text={row.iban} />}
          </div>
        </td>

        {/* SWIFT Code */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs 2xl:text-sm font-medium text-gray-700">
            {row.swiftCode || "-"}
          </span>
        </td>

        {/* Currency */}
        <td className="p-4 text-center align-middle border-t border-b border-gray-200/60">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
            {row.currencyName || row.currency || (row.currencyId ? `ID: ${row.currencyId}` : "SAR")}
          </span>
        </td>

        {/* City / Country */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs text-gray-600 font-medium">
            {[row.address?.city || row.city, row.address?.country || row.country].filter(Boolean).join(", ") || "-"}
          </span>
        </td>

        {/* Action Buttons */}
        <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
          <div className="flex items-center justify-center gap-1.5">
            {/* View */}
            <Link
              to={`/payments/banks/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
              title="View details"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>

            {/* Edit */}
            {canEdit && (
              <Link
                to={`/payments/banks/edit/${row.id}`}
                state={{ data: row, from: "/payments/banks", pageIndex, searchQuery }}
                className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Edit bank"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            )}

            {/* Delete */}
            {canDelete && (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer"
                title="Delete bank"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove "${row.bankName} - ${row.accountNumber}"?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
};

// Bank Card Component for Mobile/Grid
const BankCard = ({ row, isSelected, onSelect, pageIndex, searchQuery, fetcher }) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("banks_edit") || GetRole();
  const canDelete = GetAccess("banks_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/banks/${row.id}`, setDeleteConfirmOpen);
  };

  return (
    <div className="bg-white border border-gray-200/80 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between">
      <div>
        <div className="flex items-start justify-between gap-2 pb-3 border-b border-gray-100">
          <div>
            <Link
              to={`/payments/banks/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="text-sm font-bold text-gray-900 hover:text-emerald-600 transition-colors"
            >
              {row.bankName || "-"}
            </Link>
            <p className="text-xs text-gray-500 font-medium mt-0.5">
              {row.accountName || "-"}
            </p>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
            {row.currencyName || row.currency || "SAR"}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2.5 py-3 text-xs">
          <div>
            <span className="text-gray-400 block text-[11px]">Account Number</span>
            <div className="flex items-center font-medium text-gray-700 mt-0.5">
              <span className="truncate">{row.accountNumber || "-"}</span>
              {row.accountNumber && <CopyButton text={row.accountNumber} />}
            </div>
          </div>

          <div>
            <span className="text-gray-400 block text-[11px]">SWIFT Code</span>
            <span className="font-medium text-gray-700 block mt-0.5">
              {row.swiftCode || "-"}
            </span>
          </div>

          <div className="col-span-2">
            <span className="text-gray-400 block text-[11px]">IBAN</span>
            <div className="flex items-center font-medium text-gray-700 mt-0.5">
              <span className="truncate font-mono text-[11px]">{row.iban || "-"}</span>
              {row.iban && <CopyButton text={row.iban} />}
            </div>
          </div>

          {(row.address?.city || row.city || row.address?.country || row.country) && (
            <div className="col-span-2">
              <span className="text-gray-400 block text-[11px]">Location</span>
              <span className="text-gray-600 font-medium mt-0.5 block truncate">
                {[row.address?.city || row.city, row.address?.country || row.country].filter(Boolean).join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-1.5 pt-3 border-t border-gray-100">
        <Link
          to={`/payments/banks/view/${row.id}`}
          state={{ data: row, pageIndex, searchQuery }}
          className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
          title="View details"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </Link>

        {canEdit && (
          <Link
            to={`/payments/banks/edit/${row.id}`}
            state={{ data: row, from: "/payments/banks", pageIndex, searchQuery }}
            className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
            title="Edit bank"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </Link>
        )}

        {canDelete && (
          <button
            onClick={() => setDeleteConfirmOpen(true)}
            className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer"
            title="Delete bank"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove "${row.bankName} - ${row.accountNumber}"?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Main BanksTable Component using PremiumTable
const BanksTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  searchQuery,
  fetcher,
  dataLoading = false,
  viewMode = "table",
}) => {
  const headers = [
    { title: "Bank Name" },
    { title: "Account Name" },
    { title: "Account Number" },
    { title: "IBAN" },
    { title: "SWIFT Code" },
    { title: "Currency", align: "center" },
    { title: "City / Country" },
    { title: "Actions", align: "center" },
  ];

  return (
    <PremiumTable
      data={data}
      total={total}
      pageIndex={pageIndex}
      setPageIndex={setPageIndex}
      dataCount={dataCount}
      setDataCount={setDataCount}
      viewMode={viewMode}
      headers={headers}
      renderRow={(row, idx) => (
        <BankTableRow
          key={row.id || idx}
          row={row}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
        />
      )}
      renderCard={(row, idx) => (
        <BankCard
          key={row.id || idx}
          row={row}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
        />
      )}
      dataLoading={dataLoading}
    />
  );
};

export default BanksTable;
